import { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  apiClient,
  type EmailTemplateDto,
  type PreviewEmailTemplateResult,
  type TemplateVariableInfo,
} from '../../services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

type TemplateVariableItem = TemplateVariableInfo;

interface TemplateItem {
  id: string;
  name: string;
  notificationType: number;
  subjectTemplate: string;
  bodyTemplate: string;
  isActive: boolean;
  variables: TemplateVariableItem[];
}

interface EditorState {
  subjectTemplate: string;
  bodyTemplate: string;
  isActive: boolean;
}

function normalizeTemplate(template: EmailTemplateDto): TemplateItem {
  return {
    id: template.id,
    name: template.name,
    notificationType: template.notificationType,
    subjectTemplate: template.subjectTemplate,
    bodyTemplate: template.bodyTemplate,
    isActive: template.isActive,
    variables: template.availableVariables,
  };
}

function isIncidentTemplate(type: number): boolean {
  return type >= 0 && type <= 10;
}

export function EmailTemplatesTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading, isError, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const result = await apiClient.getEmailTemplates();
      return result.map((entry) => normalizeTemplate(entry));
    },
    enabled: user?.role === 'SystemAdmin',
  });

  const [selectedId, setSelectedId] = useState<string>('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [previewData, setPreviewData] = useState<PreviewEmailTemplateResult | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const activeSelectedId = useMemo(() => {
    if (templates.length === 0) {
      return '';
    }

    const selectedStillExists = templates.some((template) => template.id === selectedId);
    if (selectedStillExists) {
      return selectedId;
    }

    return templates[0].id;
  }, [templates, selectedId]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === activeSelectedId) ?? null,
    [templates, activeSelectedId],
  );

  const activeEditor = useMemo(() => {
    if (!selectedTemplate) {
      return null;
    }

    if (!editor || selectedTemplate.id !== selectedId) {
      return {
        subjectTemplate: selectedTemplate.subjectTemplate,
        bodyTemplate: selectedTemplate.bodyTemplate,
        isActive: selectedTemplate.isActive,
      };
    }

    return editor;
  }, [selectedTemplate, editor, selectedId]);

  const incidentTemplates = useMemo(
    () => templates.filter((template) => isIncidentTemplate(template.notificationType)),
    [templates],
  );

  const discoveryTemplates = useMemo(
    () => templates.filter((template) => !isIncidentTemplate(template.notificationType)),
    [templates],
  );

  const updateMutation = useMutation({
    mutationFn: (data: { subjectTemplate: string; bodyTemplate: string; isActive: boolean }) => {
      if (!activeSelectedId) {
        throw new Error('No template selected');
      }
      return apiClient.updateEmailTemplate(activeSelectedId, data);
    },
    onSuccess: async () => {
      addToast('Template saved successfully.', 'success');
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: () => addToast('Failed to save template.', 'error'),
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => apiClient.resetEmailTemplate(id),
    onSuccess: async () => {
      addToast('Template reset to default.', 'success');
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: () => addToast('Failed to reset template.', 'error'),
  });

  const previewMutation = useMutation({
    mutationFn: (data: { subjectTemplate: string; bodyTemplate: string; notificationType: number }) =>
      apiClient.previewEmailTemplate(data),
    onSuccess: (result) => {
      setPreviewData(result);
      setIsPreviewOpen(true);
    },
    onError: () => addToast('Failed to generate preview.', 'error'),
  });

  const selectTemplate = (template: TemplateItem) => {
    setSelectedId(template.id);
    setEditor({
      subjectTemplate: template.subjectTemplate,
      bodyTemplate: template.bodyTemplate,
      isActive: template.isActive,
    });
  };

  const handleCopyVariable = async (name: string) => {
    const token = `{{${name}}}`;
    try {
      await navigator.clipboard.writeText(token);
      addToast(`Copied ${token}`, 'success');
    } catch {
      addToast('Failed to copy variable.', 'error');
    }
  };

  if (user?.role !== 'SystemAdmin') {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
        <h3 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Access Denied</h3>
        <p className="text-yellow-600 dark:text-yellow-400">
          Only System Administrators can manage email templates.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="py-8 text-sm text-gray-500 dark:text-gray-400">Loading email templates...</div>;
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <h3 className="mb-2 font-semibold text-red-800 dark:text-red-300">Error</h3>
        <p className="text-red-600 dark:text-red-400">
          {error instanceof Error ? error.message : 'Failed to load email templates.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start">
      <div className="w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:w-[300px] lg:flex-none">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Templates</h2>
        </div>
        <div className="max-h-[44rem] space-y-4 overflow-y-auto p-3">
            <TemplateSection
              title="Incident Notifications"
              templates={incidentTemplates}
              selectedId={activeSelectedId}
              onSelect={selectTemplate}
            />
            <TemplateSection
              title="Discovery Notifications"
              templates={discoveryTemplates}
              selectedId={activeSelectedId}
              onSelect={selectTemplate}
            />
          </div>
        </div>

      <div className="w-full min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        {!selectedTemplate || !activeEditor ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Select a template to edit.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="template-name">
                Template Name
              </label>
              <input
                id="template-name"
                className="input bg-gray-100 dark:bg-gray-700"
                value={selectedTemplate.name}
                readOnly
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <span className="font-medium text-gray-900 dark:text-gray-100">Active</span>
              <button
                type="button"
                onClick={() => setEditor({ ...activeEditor, isActive: !activeEditor.isActive })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  activeEditor.isActive ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-pressed={activeEditor.isActive}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    activeEditor.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="label" htmlFor="subject-template">
                Subject Template
              </label>
              <input
                id="subject-template"
                className="input"
                value={activeEditor.subjectTemplate}
                onChange={(event) =>
                  setEditor({ ...activeEditor, subjectTemplate: event.target.value })
                }
              />
            </div>

            <div>
              <label className="label" htmlFor="body-template">
                Body Template
              </label>
              <textarea
                id="body-template"
                rows={15}
                className="input font-mono"
                value={activeEditor.bodyTemplate}
                onChange={(event) => setEditor({ ...activeEditor, bodyTemplate: event.target.value })}
              />
            </div>

            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <h3 className="mb-2 font-medium text-gray-900 dark:text-gray-100">Available Variables</h3>
              {selectedTemplate.variables.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No variables available.</p>
              ) : (
                <div className="space-y-2">
                  {selectedTemplate.variables.map((variable) => (
                    <div
                      key={variable.name}
                      className="flex items-start justify-between rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700"
                    >
                      <div>
                        <code className="text-sm text-gray-900 dark:text-gray-100">{`{{${variable.name}}}`}</code>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {variable.description || 'No description'}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary px-2 py-1 text-xs"
                        onClick={() => handleCopyVariable(variable.name)}
                      >
                        Copy
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded bg-primary-700 px-4 py-2 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-600 dark:hover:bg-primary-500"
                disabled={updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({
                      subjectTemplate: activeEditor.subjectTemplate,
                      bodyTemplate: activeEditor.bodyTemplate,
                      isActive: activeEditor.isActive,
                    })
                  }
                >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="rounded border border-red-300 px-4 py-2 text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                disabled={resetMutation.isPending}
                onClick={() => {
                  if (!selectedTemplate) {
                    return;
                  }
                  if (!window.confirm('Reset this template to default values?')) {
                    return;
                  }
                  resetMutation.mutate(selectedTemplate.id);
                }}
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset to Default'}
              </button>
              <button
                type="button"
                className="rounded bg-gray-200 px-4 py-2 text-gray-900 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                disabled={previewMutation.isPending}
                  onClick={() =>
                    previewMutation.mutate({
                      subjectTemplate: activeEditor.subjectTemplate,
                      bodyTemplate: activeEditor.bodyTemplate,
                      notificationType: selectedTemplate.notificationType,
                    })
                  }
              >
                {previewMutation.isPending ? 'Previewing...' : 'Preview'}
              </button>
            </div>
          </div>
        )}
      </div>

      {isPreviewOpen && previewData && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsPreviewOpen(false)} aria-hidden="true" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Preview</h3>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                  onClick={() => setIsPreviewOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4 p-4">
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">Rendered Subject</p>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
                    {previewData.renderedSubject}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">Rendered Body</p>
                  <div
                    className="max-h-96 overflow-auto rounded-md border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: previewData.renderedBodyHtml,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface TemplateSectionProps {
  title: string;
  templates: TemplateItem[];
  selectedId: string;
  onSelect: (template: TemplateItem) => void;
}

function TemplateSection({ title, templates, selectedId, onSelect }: TemplateSectionProps) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">{title}</h3>
      <div className="space-y-2">
        {templates.map((template) => {
          const isSelected = template.id === selectedId;
          return (
            <button
              key={template.id}
              type="button"
              onClick={() => onSelect(template)}
              className={`w-full rounded-lg border px-3 py-2 text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{template.name}</p>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    template.isActive
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >
                  {template.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
